"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { formatInTimeZone } from "date-fns-tz";
import { enUS, te } from "date-fns/locale";

import ThemeLogo from "@/components/shared/ThemeLogo";
import {
  EPAPER_HEADER_LOGO_CLASS,
  EPAPER_MASTHEAD_ENGLISH_FONT_CLASS,
  HEADER_EPAPER_BADGE_CLASS,
  HEADER_LOGO_HEIGHT,
  HEADER_LOGO_WIDTH,
  WEBSITE_NAME,
} from "@/data/constants";
import { getLabel } from "@/data/labels";

const EPAPER_TZ = "Asia/Kolkata";

function getLangFromPathname(pathname: string): AppLocale {
  const segment = pathname.split("/").filter(Boolean)[0] || "";
  if (segment.startsWith("te")) return "te";
  if (segment.startsWith("en")) return "en";
  return "te";
}

function formatEpaperHeaderDateParts(lang: AppLocale, date: Date) {
  const locale = lang === "te" ? te : enUS;
  return {
    day: formatInTimeZone(date, EPAPER_TZ, "d", { locale }),
    month: formatInTimeZone(date, EPAPER_TZ, "MMMM", { locale }),
    weekday: formatInTimeZone(date, EPAPER_TZ, "EEEE", { locale }),
  };
}

export default function EpaperHeader() {
  const pathName = usePathname() || "";
  const lang = getLangFromPathname(pathName);
  const dateParts = formatEpaperHeaderDateParts(lang, new Date());

  return (
    <div className="border-b border-[#E6E6E6] dark:border-darkBgSecondaryColor bg-white dark:bg-darkBgPrimaryColor">
      <div className="container px-3 sm:px-4">
        <div className="flex items-center justify-between gap-2 py-2.5 sm:gap-4 sm:py-3.5">
          <div className="flex min-w-0 max-w-[calc(100%-5.5rem)] items-center gap-2 sm:max-w-none sm:flex-1 sm:gap-3">
            <Link
              href="/"
              className="flex shrink-0 items-center"
              aria-label={`${WEBSITE_NAME} home`}
            >
              <ThemeLogo
                className={EPAPER_HEADER_LOGO_CLASS}
                alt={WEBSITE_NAME}
                width={HEADER_LOGO_WIDTH}
                height={HEADER_LOGO_HEIGHT}
                fetchPriority="high"
                decoding="sync"
              />
            </Link>
            <span className={HEADER_EPAPER_BADGE_CLASS}>{getLabel("en", "E_PAPER")}</span>
          </div>

          <div
            className={`flex shrink-0 items-center gap-1 text-[#4a4a4a] dark:text-gray-200 sm:gap-2 ${EPAPER_MASTHEAD_ENGLISH_FONT_CLASS}`}
            suppressHydrationWarning
            aria-label={`${dateParts.weekday}, ${dateParts.month} ${dateParts.day}`}
          >
            <span className="text-xl font-semibold leading-none tabular-nums sm:text-[34px]">
              {dateParts.day}
            </span>
            <div className="flex min-w-[3.25rem] flex-col text-[9px] font-medium uppercase leading-tight sm:min-w-0 sm:text-[11px]">
              <span className="truncate">{dateParts.month}</span>
              <span className="truncate">{dateParts.weekday}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
